import React from "react";

import { compact, isEmpty, map } from "lodash/fp";
import { useParams } from "react-router-dom";

import Highlight from "@app/analytics/MeetingReport/components/Highlight";
import UpsellOverlay from "@app/analytics/MeetingReport/components/UpsellOverlay";
import useReportActions from "@app/analytics/MeetingReport/hooks/useReportActions";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import * as Events from "@app/analytics/MeetingReport/tracking/events";
import usePreloadImages from "@app/common/hooks/usePreloadImages";
import ROUTES from "@app/common/routes";
import type { SessionHighlight } from "@app/common/types";
import { getAppUrl } from "@app/utils/utils";

import Button from "@rui/Button";
import {
  EmptyStateContainer,
  EmptyStateSubtext,
  EmptyStateTitle,
} from "@rui/EmptyStateContainer";
import { Column } from "@rui/Stack";

const getHighlightDuration = (highlight: SessionHighlight) => {
  if (!highlight.startTime || !highlight.endTime) return 0;

  return highlight.endTime.diff(highlight.startTime).as("seconds");
};

const HighlightsTab = ({
  filtered,
  highlights,
  onRemoveFilters,
  shouldUpsell,
}: {
  filtered: boolean;
  highlights: SessionHighlight[];
  onRemoveFilters: () => void;
  shouldUpsell: boolean;
}) => {
  const { sessionId } = useParams();

  usePreloadImages(compact(map("thumbnailUrl", highlights)));

  const { makeJumpToTimestamp, makeScrollToItem, shouldJumpToTimestamp } =
    useReportActions();

  const { playbackEnabled, videoReel, setVideoReelClipIndex } = useVideoStore(
    (state) => ({
      playbackEnabled: state.playbackEnabled,
      setVideoReelClipIndex: state.setVideoReelClipIndex,
      videoReel: state.videoReel,
    })
  );

  if (shouldUpsell) {
    return (
      <Column spacing={1} sx={{ mt: 1, position: "relative" }}>
        <UpsellOverlay>
          {highlights.map((highlight) => (
            <Highlight
              key={highlight.id}
              type={highlight.type}
              quote={highlight.quote}
              duration={0}
              label={highlight.description}
              timestamp={highlight.timestamp}
            />
          ))}
        </UpsellOverlay>
      </Column>
    );
  }

  const jumpToHighlight = (highlight: SessionHighlight) => {
    if (shouldJumpToTimestamp) {
      if (videoReel?.name === "highlights") {
        return () => {
          Events.playHighlight({ type: highlight.type, id: highlight.id });
          setVideoReelClipIndex(highlights.indexOf(highlight));
        };
      }

      if (highlight.relativeStartTime) {
        return makeJumpToTimestamp(highlight.relativeStartTime, {
          onJumpTo: () =>
            Events.playHighlight({ type: highlight.type, id: highlight.id }),
        });
      }
    }

    return makeScrollToItem({
      type: "paragraph",
      rawTimestamp: highlight.startTime?.toMillis(),
    });
  };

  return (
    <Column spacing={1} sx={{ mt: 1 }}>
      {isEmpty(highlights) ? (
        <EmptyStateContainer sx={{ py: 1.5 }} spacing={1.5}>
          <EmptyStateTitle>Nothing to see here</EmptyStateTitle>
          <EmptyStateSubtext>
            {filtered
              ? "Oops, there are no highlights to show for the selected filters. Try removing filters to see more."
              : "No highlights generated for this meeting"}
          </EmptyStateSubtext>
          {filtered && (
            <Button variant="gradient" onClick={onRemoveFilters}>
              Remove filters
            </Button>
          )}
        </EmptyStateContainer>
      ) : (
        <>
          {highlights.map((highlight) => (
            <Highlight
              key={highlight.id}
              type={highlight.type}
              quote={highlight.quote}
              deepLink={
                sessionId && highlight.relativeStartTime != null
                  ? getAppUrl(
                      ROUTES.meetingReportAtTimestamp(
                        sessionId,
                        Math.floor(highlight.relativeStartTime)
                      )
                    )
                  : undefined
              }
              duration={getHighlightDuration(highlight)}
              label={highlight.description}
              onClick={jumpToHighlight(highlight)}
              timestamp={highlight.timestamp}
              clipSrc={playbackEnabled ? highlight.thumbnailUrl : null}
            />
          ))}
        </>
      )}
    </Column>
  );
};

export default HighlightsTab;
